import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils.js'

/**
 * Button (shadcn/ui shape, D4).
 *
 * Every state a button can be in is defined here once — hover, focus-visible,
 * disabled, loading — because a product where some buttons show a pending state
 * and others do not is one that feels unreliable regardless of how fast it is.
 *
 * `primary` resolves to the tenant's brand colour and, critically, to
 * `text-brand-ink` — a foreground DERIVED from that colour's luminance rather
 * than assumed to be white. See lib/branding.js: without it, a tenant with a
 * light brand gets unreadable buttons and never reports it.
 *
 * Default size is `tap` (2.75rem / 44px), not a desktop-height control. D8
 * makes the field screens mobile-first, and a button that is comfortable on a
 * roof is merely generous on a desktop.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-md text-sm font-medium',
    'transition-[background-color,border-color,color,box-shadow] duration-150',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand text-brand-ink shadow-card hover:bg-brand-hover',
        secondary: 'bg-surface text-ink border border-line-strong shadow-card hover:bg-surface-sunken',
        ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
        danger: 'bg-danger text-white shadow-card hover:bg-danger/90',
        link: 'text-brand underline-offset-4 hover:underline'
      },
      size: {
        tap: 'h-tap px-4 py-2',
        sm: 'h-9 rounded px-3 text-sm',
        icon: 'h-tap w-tap',
        'icon-sm': 'h-9 w-9 rounded'
      }
    },
    defaultVariants: { variant: 'primary', size: 'tap' }
  }
)

/**
 * @param {object} props
 * @param {'primary'|'secondary'|'ghost'|'danger'|'link'} [props.variant]
 * @param {'tap'|'sm'|'icon'|'icon-sm'} [props.size]
 * @param {boolean} [props.loading] shows a spinner and blocks interaction
 * @param {boolean} [props.asChild] render as the child element (Radix Slot)
 */
export const Button = React.forwardRef(function Button (
  { className, variant, size, loading = false, asChild = false, children, disabled, ...props },
  ref
) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      // `disabled` is meaningless on an anchor and React warns about it, so it
      // is only passed to a real <button>.
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {/*
        asChild MUST render exactly one child. Radix's Slot merges this
        component's props onto the child element, and it throws — taking the
        whole tree down with it — if it is handed more than one. So the spinner
        is only added in the plain-<button> case.

        That is not a workaround: a slotted button delegates its rendering to
        the child (a react-router <Link>, usually), and injecting a sibling
        icon into someone else's element is exactly what Slot is designed to
        prevent. Links navigate; they do not have a pending state.
      */}
      {asChild
        ? children
        : (
          <>
            {loading && <Loader2 className="animate-spin" aria-hidden="true" />}
            {children}
          </>
          )}
    </Comp>
  )
})

export { buttonVariants }
